import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, ArrowRight, Calendar, Clock, Share2 } from 'lucide-react';
import { getBlogById, getBlogsBySeries } from '../data/blogs';

// Blog content for each part
const blogContent = {
  'android-hardware-part1-architecture': `
![Android Architecture Layers](https://static.prod-images.emergentagent.com/jobs/aa436dfd-e6d2-4793-b915-9d5cfca93252/images/bcf609ba4786cdc1efb09c692904002f98dc371ed0dbfb9aa349e3535f1c81fd.png)

## Introduction

In Android, **applications never directly access hardware**. This is not a limitation — it's a deliberate architectural decision that enables security, stability, and portability across thousands of different devices.

Every hardware interaction traverses a well-defined stack of platform layers, each enforcing security boundaries and abstraction. Understanding this architecture is fundamental to platform-level Android development.

**Target Platform:** AOSP 15 + Raspberry Pi 5

---

## The Android Platform Stack

Here's what each layer does and the language it's written in:

| Layer | Language | Responsibility |
|-------|----------|----------------|
| **Application** | Kotlin/Java | User interface, business logic |
| **Framework API** | Java | Stable public APIs (\`android.*\` packages) |
| **System Service** | Java | Runs in \`system_server\`, manages hardware access |
| **HAL Service** | C++ | Vendor process, translates to hardware commands |
| **Linux Kernel** | C | Device drivers, sysfs GPIO interface |
| **Hardware** | - | Physical GPIO pins, LEDs, sensors |

---

## Why This Architecture Exists

### Security Through Layers

Apps cannot bypass the platform stack. Every hardware access is:
- **Mediated** - Goes through authorized platform components
- **Permissioned** - Checked against app permissions
- **Logged** - Can be audited for security analysis

### Portability

The same app binary runs on thousands of different devices because:
- Apps talk to stable Framework APIs
- Hardware differences are hidden in the HAL layer
- OEMs customize HAL without changing apps

### Stability

If one app crashes:
- It doesn't take down the system
- Other apps continue running
- Hardware state is managed by system services

---

## The Request Flow Overview

When you press a button in an app to turn on an LED, here's what happens:

\`\`\`
┌─────────────────────────────────────────────────────────────┐
│  1. APPLICATION (Kotlin)                                     │
│     User presses button → calls ledManager.setLedState(true) │
│                          │                                   │
│  2. FRAMEWORK API (Java) ▼                                   │
│     LedGroupServiceManager forwards via Binder IPC           │
│                          │                                   │
│  3. SYSTEM SERVICE (Java)▼                                   │
│     LedGroupService (in system_server) calls HAL             │
│                          │                                   │
│  4. HAL SERVICE (C++)    ▼                                   │
│     Writes to /sys/class/gpio/gpio586/value                  │
│                          │                                   │
│  5. LINUX KERNEL (C)     ▼                                   │
│     pinctrl-rp1 driver sets GPIO pin HIGH                    │
│                          │                                   │
│  6. HARDWARE             ▼                                   │
│     GPIO pin outputs 3.3V → LED turns ON                     │
└─────────────────────────────────────────────────────────────┘
\`\`\`

---

## Key Takeaway

The layered architecture is your friend, not your enemy. It provides:

1. **Clear boundaries** - Each layer has a specific job
2. **Testability** - You can test each layer independently
3. **Maintainability** - Change one layer without affecting others
4. **Security** - No shortcuts to hardware access

In the next part, we'll start coding — building the Kotlin application and Framework API layer.
`,

  'android-hardware-part2-app-framework': `
## Application Layer (Kotlin)

The application is the user's entry point. It calls system services through Android's standard \`Context.getSystemService()\` pattern — the same way you'd get \`LocationManager\` or \`NotificationManager\`.

### Complete Application Code

\`\`\`kotlin
// MainActivity.kt
package com.example.ledcontrol

import android.app.Activity
import android.os.Bundle
import android.widget.ToggleButton
import android.widget.TextView
import android.widget.Toast
import android.app.led_group.LedGroupServiceManager
import android.content.Context

class MainActivity : Activity() {
    
    private var ledManager: LedGroupServiceManager? = null
    private lateinit var statusText: TextView
    
    override fun onCreate(savedInstanceState: Bundle?) {
        super.onCreate(savedInstanceState)
        setContentView(R.layout.activity_main)
        
        statusText = findViewById(R.id.status_text)
        
        // Get the system service
        // This is exactly like getting LocationManager or any other system service
        ledManager = getSystemService(Context.LED_GROUP_SERVICE) 
            as? LedGroupServiceManager
        
        if (ledManager == null) {
            statusText.text = "LED Service not available"
            Toast.makeText(this, "LED HAL not installed", Toast.LENGTH_LONG).show()
            return
        }
        
        // Wire up the toggle button
        val toggleLed = findViewById<ToggleButton>(R.id.toggle_led)
        toggleLed.setOnCheckedChangeListener { _, isChecked ->
            // This single call travels through 5 layers to reach hardware
            val result = ledManager?.setLedState(isChecked) ?: -1
            
            if (result == 0) {
                statusText.text = if (isChecked) "LED: ON" else "LED: OFF"
            } else {
                statusText.text = "Error: $result"
                Toast.makeText(this, "Failed to set LED state", Toast.LENGTH_SHORT).show()
            }
        }
    }
}
\`\`\`

### Layout XML

\`\`\`xml
<!-- res/layout/activity_main.xml -->
<?xml version="1.0" encoding="utf-8"?>
<LinearLayout xmlns:android="http://schemas.android.com/apk/res/android"
    android:layout_width="match_parent"
    android:layout_height="match_parent"
    android:orientation="vertical"
    android:gravity="center"
    android:padding="32dp">

    <TextView
        android:id="@+id/status_text"
        android:layout_width="wrap_content"
        android:layout_height="wrap_content"
        android:text="LED: OFF"
        android:textSize="24sp"
        android:layout_marginBottom="32dp"/>

    <ToggleButton
        android:id="@+id/toggle_led"
        android:layout_width="wrap_content"
        android:layout_height="wrap_content"
        android:textOn="Turn OFF"
        android:textOff="Turn ON"/>

</LinearLayout>
\`\`\`

---

## Framework API Layer (Java)

The Framework provides a stable API that hides all implementation details. Applications interact with \`LedGroupServiceManager\`, never with the raw HAL or Binder directly.

### Service Manager Implementation

\`\`\`java
// frameworks/base/core/java/android/app/led_group/LedGroupServiceManager.java
package android.app.led_group;

import android.annotation.SystemService;
import android.content.Context;
import android.os.RemoteException;
import android.util.Log;

/**
 * Manager class for controlling LED hardware.
 * 
 * Obtain an instance via Context.getSystemService(Context.LED_GROUP_SERVICE).
 */
@SystemService(Context.LED_GROUP_SERVICE)
public final class LedGroupServiceManager {
    
    private static final String TAG = "LedGroupServiceManager";
    
    // This is the Binder proxy to the actual service in system_server
    private final ILedGroupServiceManager mService;
    
    /** @hide */
    public LedGroupServiceManager(ILedGroupServiceManager service) {
        mService = service;
    }
    
    /**
     * Sets the LED state.
     * 
     * @param state true to turn ON, false to turn OFF
     * @return 0 on success, negative error code on failure
     */
    public int setLedState(boolean state) {
        try {
            // This call crosses the Binder IPC boundary
            // App process → system_server process
            return mService.setLedState(state);
        } catch (RemoteException e) {
            Log.e(TAG, "Failed to communicate with LED service", e);
            return -1;
        }
    }
    
    /**
     * Gets the current LED state.
     * 
     * @return true if LED is ON, false if OFF
     */
    public boolean getLedState() {
        try {
            return mService.getLedState();
        } catch (RemoteException e) {
            Log.e(TAG, "Failed to get LED state", e);
            return false;
        }
    }
}
\`\`\`

### AIDL Interface Definition

AIDL (Android Interface Definition Language) defines the IPC contract between app and service:

\`\`\`java
// frameworks/base/core/java/android/app/led_group/ILedGroupServiceManager.aidl
package android.app.led_group;

/**
 * System service interface for LED control.
 * @hide
 */
interface ILedGroupServiceManager {
    /**
     * Sets the LED state.
     * @param state true for ON, false for OFF
     * @return 0 on success, negative on error
     */
    int setLedState(boolean state);
    
    /**
     * Gets the current LED state.
     * @return true if ON, false if OFF
     */
    boolean getLedState();
}
\`\`\`

---

## Understanding Binder IPC

When the app calls \`ledManager.setLedState(true)\`:

1. **Serialization**: The boolean \`true\` is serialized into a Parcel
2. **Kernel Transition**: The Parcel is sent to the Binder kernel driver
3. **Process Switch**: Kernel wakes up the target thread in system_server
4. **Deserialization**: system_server deserializes the Parcel
5. **Method Call**: The actual \`setLedState()\` runs in system_server

This all happens transparently — your code just looks like a normal method call.

---

## What's Next

In Part 3, we'll implement the System Service that runs inside \`system_server\` and handles the Binder calls from applications.
`,

  'android-hardware-part3-system-service': `
## System Service Layer

System Services run inside \`system_server\` — Android's central daemon process. They have elevated privileges and can communicate with HAL services that apps cannot access directly.

![HAL Request Flow](https://static.prod-images.emergentagent.com/jobs/aa436dfd-e6d2-4793-b915-9d5cfca93252/images/f8049e247fb21e487db68a861cc69b218242ca2b108621a84e20ff8e44ff08c0.png)

---

## Complete System Service Implementation

\`\`\`java
// frameworks/base/services/core/java/com/android/server/led_group/LedGroupService.java
package com.android.server.led_group;

import android.app.led_group.ILedGroupServiceManager;
import android.content.Context;
import android.hardware.led_group.ILedGroup;
import android.os.Binder;
import android.os.RemoteException;
import android.os.ServiceManager;
import android.util.Log;
import com.android.server.SystemService;

/**
 * System service that manages LED hardware access.
 * 
 * This service:
 * 1. Runs inside system_server with elevated privileges
 * 2. Connects to the HAL service on startup
 * 3. Receives requests from apps via Binder
 * 4. Forwards requests to the HAL
 * 5. Enforces permissions
 */
public class LedGroupService extends SystemService {
    
    private static final String TAG = "LedGroupService";
    
    // The HAL service name as registered in the manifest
    private static final String HAL_SERVICE_NAME = 
        "android.hardware.led_group.ILedGroup/default";
    
    // Connection to the HAL (native C++ service)
    private ILedGroup mHal;
    
    public LedGroupService(Context context) {
        super(context);
    }
    
    @Override
    public void onStart() {
        Log.i(TAG, "Starting LedGroupService...");
        
        // Wait for and connect to the HAL service
        // This blocks until the HAL is available
        mHal = ILedGroup.Stub.asInterface(
            ServiceManager.waitForService(HAL_SERVICE_NAME)
        );
        
        if (mHal == null) {
            Log.e(TAG, "Failed to connect to LED HAL service");
            return;
        }
        
        Log.i(TAG, "Connected to LED HAL successfully");
        
        // Publish this service so apps can find it via getSystemService()
        publishBinderService(Context.LED_GROUP_SERVICE, new BinderService());
        
        Log.i(TAG, "LedGroupService started and published");
    }
    
    @Override
    public void onBootPhase(int phase) {
        if (phase == SystemService.PHASE_BOOT_COMPLETED) {
            Log.i(TAG, "Boot completed, LED service ready");
        }
    }
    
    /**
     * Inner class that handles incoming Binder calls from apps.
     * 
     * Each method here corresponds to a method in the AIDL interface.
     */
    private final class BinderService extends ILedGroupServiceManager.Stub {
        
        @Override
        public int setLedState(boolean state) {
            // Log the caller for debugging
            final int callingUid = Binder.getCallingUid();
            final int callingPid = Binder.getCallingPid();
            Log.d(TAG, "setLedState(" + state + ") from uid=" + callingUid 
                + " pid=" + callingPid);
            
            // Enforce permission check
            // Apps must have this permission in their manifest
            getContext().enforceCallingOrSelfPermission(
                "android.permission.CONTROL_LED",
                "Must have CONTROL_LED permission to control LED"
            );
            
            // Check if HAL is available
            if (mHal == null) {
                Log.e(TAG, "HAL not available");
                return -1;
            }
            
            try {
                // Clear calling identity for the HAL call
                // This is important for security
                final long token = Binder.clearCallingIdentity();
                try {
                    // Forward to HAL - this is another IPC boundary
                    // system_server → HAL vendor process
                    int[] result = new int[1];
                    mHal.setLedState(state, result);
                    
                    Log.d(TAG, "HAL returned: " + result[0]);
                    return result[0];
                } finally {
                    Binder.restoreCallingIdentity(token);
                }
            } catch (RemoteException e) {
                Log.e(TAG, "HAL call failed", e);
                return -2;
            }
        }
        
        @Override
        public boolean getLedState() {
            if (mHal == null) {
                return false;
            }
            
            try {
                final long token = Binder.clearCallingIdentity();
                try {
                    return mHal.getLedState();
                } finally {
                    Binder.restoreCallingIdentity(token);
                }
            } catch (RemoteException e) {
                Log.e(TAG, "Failed to get LED state from HAL", e);
                return false;
            }
        }
    }
}
\`\`\`

---

## Registering the Service

The service must be registered in \`SystemServer.java\`:

\`\`\`java
// frameworks/base/services/java/com/android/server/SystemServer.java
// Add in startOtherServices() method:

private void startOtherServices() {
    // ... other services ...
    
    traceBeginAndSlog("StartLedGroupService");
    mSystemServiceManager.startService(LedGroupService.class);
    traceEnd();
    
    // ... other services ...
}
\`\`\`

---

## Adding the Permission

Define the permission in the framework:

\`\`\`xml
<!-- frameworks/base/core/res/AndroidManifest.xml -->
<permission 
    android:name="android.permission.CONTROL_LED"
    android:label="Control LED hardware"
    android:description="Allows the app to control LED hardware"
    android:protectionLevel="normal" />
\`\`\`

---

## Key Concepts

### Binder.clearCallingIdentity()

When the System Service calls the HAL, it should do so with its own identity, not the app's. This prevents the HAL from making decisions based on which app initiated the request.

### Permission Enforcement

Always check permissions before performing hardware operations. This is your security boundary.

### HAL Connection

The \`ServiceManager.waitForService()\` call blocks until the HAL is available. This ensures we don't try to use the HAL before it's ready.

---

## What's Next

In Part 4, we'll implement the HAL service in C++ — the layer that actually talks to the hardware.
`,

  'android-hardware-part4-hal-implementation': `
![Custom HAL Implementation](https://static.prod-images.emergentagent.com/jobs/aa436dfd-e6d2-4793-b915-9d5cfca93252/images/c91be4877c7d143f34ee8623fdff88f5e7f19f3f823f32d9af69add0d38e49e7.png)

## HAL Interface Definition (AIDL)

The HAL interface is defined in AIDL, which generates both Java stubs (for the System Service) and C++ stubs (for the HAL implementation).

### Interface Definition

\`\`\`java
// hardware/interfaces/led_group/aidl/android/hardware/led_group/ILedGroup.aidl
package android.hardware.led_group;

/**
 * HAL interface for LED control.
 * 
 * This interface is implemented by the vendor HAL service
 * and called by the System Service.
 */
@VintfStability
interface ILedGroup {
    /**
     * Sets the LED state.
     * 
     * @param state true for ON, false for OFF
     * @param result output parameter: 0 = success, negative = error
     */
    void setLedState(in boolean state, out int[] result);
    
    /**
     * Gets the current LED state.
     * 
     * @return true if LED is ON, false if OFF
     */
    boolean getLedState();
}
\`\`\`

### Build Configuration

\`\`\`json
// hardware/interfaces/led_group/aidl/Android.bp
aidl_interface {
    name: "android.hardware.led_group",
    vendor_available: true,
    srcs: ["android/hardware/led_group/*.aidl"],
    stability: "vintf",
    backend: {
        java: {
            sdk_version: "module_current",
        },
        cpp: {
            enabled: true,
        },
        ndk: {
            enabled: true,
        },
    },
    versions: ["1"],
}
\`\`\`

---

## HAL Implementation (C++)

### Header File

\`\`\`cpp
// device/brcm/rpi5/led_group/impl/LedGroup.h
#pragma once

#include <aidl/android/hardware/led_group/BnLedGroup.h>
#include <atomic>
#include <string>

namespace aidl::android::hardware::led_group {

class LedGroup : public BnLedGroup {
public:
    LedGroup();
    ~LedGroup();
    
    // AIDL interface implementation
    ndk::ScopedAStatus setLedState(bool state, 
                                   std::vector<int32_t>* result) override;
    ndk::ScopedAStatus getLedState(bool* result) override;

private:
    // GPIO control methods
    bool initGpio();
    bool writeGpio(const char* path, const char* value);
    bool readGpio(const char* path, std::string& value);
    
    // Current state tracking
    std::atomic<bool> mCurrentState{false};
    bool mGpioInitialized{false};
    
    // Raspberry Pi 5 GPIO paths
    // IMPORTANT: BCM GPIO17 = sysfs gpio586 on RPi5
    // The RP1 chip has a base offset of 569
    static constexpr const char* GPIO_EXPORT_PATH = "/sys/class/gpio/export";
    static constexpr const char* GPIO_UNEXPORT_PATH = "/sys/class/gpio/unexport";
    static constexpr const char* GPIO_DIRECTION_PATH = "/sys/class/gpio/gpio586/direction";
    static constexpr const char* GPIO_VALUE_PATH = "/sys/class/gpio/gpio586/value";
    static constexpr int GPIO_NUMBER = 586;  // BCM17 + 569
};

}  // namespace aidl::android::hardware::led_group
\`\`\`

### Implementation File

\`\`\`cpp
// device/brcm/rpi5/led_group/impl/LedGroup.cpp
#include "LedGroup.h"

#include <android-base/logging.h>
#include <fstream>
#include <sstream>

namespace aidl::android::hardware::led_group {

LedGroup::LedGroup() {
    LOG(INFO) << "LedGroup HAL initializing...";
    mGpioInitialized = initGpio();
    
    if (mGpioInitialized) {
        LOG(INFO) << "GPIO initialized successfully";
    } else {
        LOG(ERROR) << "GPIO initialization failed";
    }
}

LedGroup::~LedGroup() {
    LOG(INFO) << "LedGroup HAL shutting down";
    
    // Turn off LED on shutdown
    if (mGpioInitialized) {
        writeGpio(GPIO_VALUE_PATH, "0");
    }
}

bool LedGroup::initGpio() {
    // Step 1: Export the GPIO pin
    // This makes it available in sysfs
    std::ofstream exportFile(GPIO_EXPORT_PATH);
    if (exportFile.is_open()) {
        exportFile << GPIO_NUMBER;
        exportFile.close();
        LOG(INFO) << "Exported GPIO" << GPIO_NUMBER;
    }
    // Note: Export may fail if already exported - that's OK
    
    // Step 2: Set direction to output
    // Small delay to let sysfs create the files
    usleep(100000);  // 100ms
    
    if (!writeGpio(GPIO_DIRECTION_PATH, "out")) {
        LOG(ERROR) << "Failed to set GPIO direction";
        return false;
    }
    
    // Step 3: Set initial value to LOW (LED off)
    if (!writeGpio(GPIO_VALUE_PATH, "0")) {
        LOG(ERROR) << "Failed to set initial GPIO value";
        return false;
    }
    
    return true;
}

bool LedGroup::writeGpio(const char* path, const char* value) {
    std::ofstream file(path);
    if (!file.is_open()) {
        LOG(ERROR) << "Failed to open for writing: " << path;
        return false;
    }
    
    file << value;
    file.close();
    
    if (file.fail()) {
        LOG(ERROR) << "Failed to write to: " << path;
        return false;
    }
    
    LOG(DEBUG) << "Wrote '" << value << "' to " << path;
    return true;
}

bool LedGroup::readGpio(const char* path, std::string& value) {
    std::ifstream file(path);
    if (!file.is_open()) {
        LOG(ERROR) << "Failed to open for reading: " << path;
        return false;
    }
    
    std::getline(file, value);
    file.close();
    return true;
}

ndk::ScopedAStatus LedGroup::setLedState(bool state, 
                                          std::vector<int32_t>* result) {
    LOG(INFO) << "setLedState called: " << (state ? "ON" : "OFF");
    
    // Resize result vector to hold one element
    result->resize(1);
    
    // Check if GPIO is initialized
    if (!mGpioInitialized) {
        LOG(ERROR) << "GPIO not initialized";
        (*result)[0] = -1;
        return ndk::ScopedAStatus::ok();
    }
    
    // Write to GPIO
    // "1" = HIGH voltage (3.3V) = LED ON
    // "0" = LOW voltage (0V) = LED OFF
    const char* value = state ? "1" : "0";
    
    if (writeGpio(GPIO_VALUE_PATH, value)) {
        mCurrentState.store(state);
        (*result)[0] = 0;  // Success
        LOG(INFO) << "LED state set successfully";
    } else {
        (*result)[0] = -2;  // Write failed
        LOG(ERROR) << "Failed to set LED state";
    }
    
    return ndk::ScopedAStatus::ok();
}

ndk::ScopedAStatus LedGroup::getLedState(bool* result) {
    *result = mCurrentState.load();
    LOG(DEBUG) << "getLedState: " << (*result ? "ON" : "OFF");
    return ndk::ScopedAStatus::ok();
}

}  // namespace aidl::android::hardware::led_group
\`\`\`

---

## Service Entry Point

\`\`\`cpp
// device/brcm/rpi5/led_group/impl/service.cpp
#include "LedGroup.h"

#include <android-base/logging.h>
#include <android/binder_manager.h>
#include <android/binder_process.h>

using aidl::android::hardware::led_group::LedGroup;

int main() {
    // Set up logging
    android::base::SetDefaultTag("LedGroupHAL");
    android::base::SetMinimumLogSeverity(android::base::INFO);
    
    LOG(INFO) << "LED HAL Service starting...";
    
    // Initialize the Binder thread pool
    // 0 means use the default number of threads
    ABinderProcess_setThreadPoolMaxThreadCount(0);
    
    // Create the HAL service instance
    std::shared_ptr<LedGroup> ledHal = 
        ndk::SharedRefBase::make<LedGroup>();
    
    // Build the service name
    // Format: <interface>/<instance>
    const std::string serviceName = 
        std::string() + LedGroup::descriptor + "/default";
    
    LOG(INFO) << "Registering service: " << serviceName;
    
    // Register with the Service Manager
    binder_status_t status = AServiceManager_addService(
        ledHal->asBinder().get(),
        serviceName.c_str()
    );
    
    if (status != STATUS_OK) {
        LOG(ERROR) << "Failed to register service: " << status;
        return -1;
    }
    
    LOG(INFO) << "LED HAL Service registered successfully";
    
    // Enter the Binder event loop
    // This blocks forever, processing incoming requests
    ABinderProcess_joinThreadPool();
    
    // Never reached
    return 0;
}
\`\`\`

---

## Raspberry Pi 5 GPIO Mapping

**Critical Note:** Raspberry Pi 5 uses the RP1 south bridge chip, which changes GPIO numbering!

\`\`\`
sysfs_gpio_number = BCM_GPIO_number + 569 (RP1 base offset)

Examples:
┌─────────────┬────────────┬──────────────┐
│ Physical Pin│ BCM GPIO   │ sysfs GPIO   │
├─────────────┼────────────┼──────────────┤
│ Pin 11      │ GPIO17     │ gpio586      │
│ Pin 18      │ GPIO24     │ gpio593      │
│ Pin 22      │ GPIO25     │ gpio594      │
└─────────────┴────────────┴──────────────┘
\`\`\`

### Wiring Diagram

\`\`\`
RPi5 Pin 11 (GPIO17/gpio586)
        │
        └──── [330Ω Resistor] ──── LED (+) ──── LED (-) ──── Pin 9 (GND)
\`\`\`

---

## What's Next

In Part 5, we'll configure SELinux policies and init scripts — the critical security and startup configuration that makes everything work together.
`,

  'android-hardware-part5-selinux-init': `
## Init Script Configuration

The HAL service needs an init script to start at boot. Android's init system reads \`.rc\` files from vendor partitions.

### Service Definition

\`\`\`bash
# device/brcm/rpi5/led_group/android.hardware.led_group-service.rc

# Define the HAL service
service vendor.led_group /vendor/bin/hw/android.hardware.led_group-service
    class hal
    user system
    group system

# GPIO setup runs at boot with root privileges
on boot
    # Export GPIO586 (BCM GPIO17 on RPi5)
    write /sys/class/gpio/export 586
    
    # Wait for sysfs to create the files
    wait /sys/class/gpio/gpio586/direction 1
    
    # Set direction to output
    write /sys/class/gpio/gpio586/direction out
    
    # Set initial value to LOW (LED off)
    write /sys/class/gpio/gpio586/value 0
    
    # Set permissions so the HAL service can access
    chmod 0666 /sys/class/gpio/gpio586/value
    chmod 0666 /sys/class/gpio/gpio586/direction
    chown system system /sys/class/gpio/gpio586/value
    chown system system /sys/class/gpio/gpio586/direction
\`\`\`

### Key Points

- **class hal**: Groups this service with other HAL services
- **user system**: Runs as the system user, not root
- **on boot**: GPIO export runs with root privileges before service starts
- **chmod/chown**: Grants the system user access to GPIO files

---

## SELinux Policy

SELinux is mandatory on Android. Without correct policy, your HAL cannot access any files — including GPIO.

### Domain Definition

\`\`\`bash
# device/brcm/rpi5/sepolicy/hal_led_group_default.te

# Define the HAL domain type
type hal_led_group_default, domain;
type hal_led_group_default_exec, exec_type, vendor_file_type, file_type;

# Allow init to start this service
init_daemon_domain(hal_led_group_default)

# Allow Binder IPC (required for AIDL)
binder_use(hal_led_group_default)

# Register as a HAL server
hal_server_domain(hal_led_group_default, hal_led_group)

# Allow access to GPIO sysfs files
allow hal_led_group_default sysfs_gpio:dir { open read search getattr };
allow hal_led_group_default sysfs_gpio:file { open read write getattr };

# Allow logging
allow hal_led_group_default kmsg_device:chr_file { open write };
\`\`\`

### File Contexts

\`\`\`bash
# device/brcm/rpi5/sepolicy/file_contexts

# Label the HAL executable
/vendor/bin/hw/android\\.hardware\\.led_group-service  u:object_r:hal_led_group_default_exec:s0
\`\`\`

### GPIO Sysfs Contexts

\`\`\`bash
# device/brcm/rpi5/sepolicy/genfs_contexts

# Label GPIO sysfs files
genfscon sysfs /class/gpio u:object_r:sysfs_gpio:s0
genfscon sysfs /devices/platform/soc/1f000d0000.gpio u:object_r:sysfs_gpio:s0
\`\`\`

---

## VINTF Manifest

The HAL must be declared in the device manifest for the framework to find it.

\`\`\`xml
<!-- device/brcm/rpi5/manifest.xml -->
<manifest version="1.0" type="device">
    <hal format="aidl">
        <name>android.hardware.led_group</name>
        <version>1</version>
        <interface>
            <name>ILedGroup</name>
            <instance>default</instance>
        </interface>
    </hal>
</manifest>
\`\`\`

---

## Complete Request/Response Flow

Here's the exact sequence when an app turns on the LED:

\`\`\`
┌─────────────────────────────────────────────────────────────────────────────┐
│                           REQUEST FLOW (App → Hardware)                      │
├─────────────────────────────────────────────────────────────────────────────┤
│                                                                              │
│  1. APP (Kotlin) - com.example.ledcontrol                                    │
│     └─> ledManager.setLedState(true)                                         │
│         │                                                                    │
│  2. FRAMEWORK (Java) - android.app.led_group                                 │
│     └─> mService.setLedState(true)                                           │
│         │  [Binder IPC: app process → system_server]                         │
│         │                                                                    │
│  3. SYSTEM SERVICE (Java) - com.android.server.led_group                     │
│     └─> mHal.setLedState(true, result)                                       │
│         │  [AIDL IPC: system_server → HAL vendor process]                    │
│         │                                                                    │
│  4. HAL SERVICE (C++) - aidl::android::hardware::led_group                   │
│     └─> writeGpio("/sys/class/gpio/gpio586/value", "1")                      │
│         │  [File write: user space → kernel]                                 │
│         │                                                                    │
│  5. LINUX KERNEL (C) - pinctrl-rp1 driver                                    │
│     └─> Sets BCM GPIO17 register to HIGH                                     │
│         │  [Register write: kernel → hardware]                               │
│         │                                                                    │
│  6. HARDWARE - Raspberry Pi 5 GPIO                                           │
│     └─> Pin 11 outputs 3.3V → LED illuminates                                │
│                                                                              │
├─────────────────────────────────────────────────────────────────────────────┤
│                          RESPONSE FLOW (Hardware → App)                      │
├─────────────────────────────────────────────────────────────────────────────┤
│                                                                              │
│  6. HARDWARE: Voltage level set (instantaneous)                              │
│     │                                                                        │
│  5. KERNEL: sysfs write() returns 0 (success)                                │
│     │                                                                        │
│  4. HAL: Sets result[0] = 0, returns ScopedAStatus::ok()                     │
│     │                                                                        │
│  3. SYSTEM SERVICE: Returns int 0 via Binder                                 │
│     │                                                                        │
│  2. FRAMEWORK: Returns int 0 to app                                          │
│     │                                                                        │
│  1. APP: setLedState() returns 0, updates UI                                 │
│                                                                              │
└─────────────────────────────────────────────────────────────────────────────┘
\`\`\`

---

## Build and Test Commands

\`\`\`bash
# Build AOSP with custom HAL
source build/envsetup.sh
lunch rpi5-userdebug
m android.hardware.led_group-service

# Flash to device
adb reboot bootloader
fastboot flashall

# Or push directly (dev only)
adb root
adb remount
adb push out/target/product/rpi5/vendor/bin/hw/android.hardware.led_group-service /vendor/bin/hw/
adb shell restorecon /vendor/bin/hw/android.hardware.led_group-service
adb shell stop
adb shell start

# Verify service is running
adb shell service list | grep led_group

# Check SELinux denials
adb shell dmesg | grep -i "avc.*denied"

# View HAL logs
adb logcat -s LedGroupHAL:* LedGroupService:*
\`\`\`

---

## Common SELinux Issues

| Error | Cause | Fix |
|-------|-------|-----|
| \`avc: denied { open }\` | Missing file permission | Add \`allow\` rule in .te file |
| \`avc: denied { write }\` | Can read but not write | Add \`write\` to allow rule |
| \`init: cannot find\` | Wrong file context | Check file_contexts path regex |
| Service won't start | exec type missing | Ensure \`exec_type\` in type definition |

---

## What's Next

In Part 6, we'll extend this knowledge to Android Automotive, implementing a custom Vehicle HAL (VHAL) property that controls hazard lights.
`,

  'android-hardware-part6-automotive-vhal': `
![Automotive VHAL Flow](https://static.prod-images.emergentagent.com/jobs/aa436dfd-e6d2-4793-b915-9d5cfca93252/images/e55c6275448bb335cb17d299b01c51a3e2972260ca70350c7be760b22708a0d2.png)

## Android Automotive VHAL

In Android Automotive, vehicle properties are managed through the **Vehicle HAL (VHAL)**. Unlike standard HALs with direct method calls, VHAL uses a **property-based model** where everything is a key-value pair.

---

## The Property-Based Model

Instead of methods like \`setHazardLights(true)\`, VHAL uses:

\`\`\`
setProperty(HAZARD_LIGHTS_SWITCH, 1)  // 1 = ON
setProperty(HAZARD_LIGHTS_SWITCH, 0)  // 0 = OFF
\`\`\`

This abstraction allows:
- Uniform API for thousands of vehicle properties
- Easy subscription/notification model
- Property persistence and caching
- Area-based values (driver zone, passenger zone, etc.)

---

## Automotive Application (Kotlin)

\`\`\`kotlin
// packages/apps/Car/HazardControl/src/com/example/hazard/MainActivity.kt
package com.example.hazard

import android.app.Activity
import android.car.Car
import android.car.VehiclePropertyIds
import android.car.hardware.CarPropertyValue
import android.car.hardware.property.CarPropertyManager
import android.os.Bundle
import android.util.Log
import android.widget.ToggleButton
import android.widget.TextView

class MainActivity : Activity() {
    
    companion object {
        private const val TAG = "HazardControl"
    }
    
    private var car: Car? = null
    private var propertyManager: CarPropertyManager? = null
    private lateinit var statusText: TextView
    
    override fun onCreate(savedInstanceState: Bundle?) {
        super.onCreate(savedInstanceState)
        setContentView(R.layout.activity_main)
        
        statusText = findViewById(R.id.status_text)
        
        // Connect to Car Service
        car = Car.createCar(this)
        propertyManager = car?.getCarManager(Car.PROPERTY_SERVICE) 
            as? CarPropertyManager
        
        if (propertyManager == null) {
            statusText.text = "Car Service not available"
            return
        }
        
        setupHazardToggle()
        registerPropertyCallback()
    }
    
    private fun setupHazardToggle() {
        val hazardToggle = findViewById<ToggleButton>(R.id.hazard_toggle)
        
        hazardToggle.setOnCheckedChangeListener { _, isChecked ->
            try {
                // Set the HAZARD_LIGHTS_SWITCH property
                // This call goes: App → CarPropertyManager → CarService → VHAL → GPIO
                propertyManager?.setIntProperty(
                    VehiclePropertyIds.HAZARD_LIGHTS_SWITCH,
                    0,  // Area ID (0 = global/all areas)
                    if (isChecked) 1 else 0  // 1 = ON, 0 = OFF
                )
                Log.i(TAG, "Hazard lights set to: $isChecked")
            } catch (e: Exception) {
                Log.e(TAG, "Failed to set hazard lights", e)
                statusText.text = "Error: \${e.message}"
            }
        }
    }
    
    private fun registerPropertyCallback() {
        // Register for property change notifications
        // This fires when ANY source changes the hazard lights
        propertyManager?.registerCallback(
            object : CarPropertyManager.CarPropertyEventCallback {
                
                override fun onChangeEvent(value: CarPropertyValue<*>) {
                    if (value.propertyId == VehiclePropertyIds.HAZARD_LIGHTS_SWITCH) {
                        val isOn = (value.value as Int) == 1
                        Log.i(TAG, "Hazard property changed: $isOn")
                        
                        runOnUiThread {
                            statusText.text = if (isOn) "HAZARD: ON" else "HAZARD: OFF"
                            findViewById<ToggleButton>(R.id.hazard_toggle).isChecked = isOn
                        }
                    }
                }
                
                override fun onErrorEvent(propertyId: Int, areaId: Int) {
                    Log.e(TAG, "Property error: $propertyId, area: $areaId")
                }
            },
            VehiclePropertyIds.HAZARD_LIGHTS_SWITCH,
            CarPropertyManager.SENSOR_RATE_ONCHANGE
        )
    }
    
    override fun onDestroy() {
        super.onDestroy()
        car?.disconnect()
    }
}
\`\`\`

---

## VHAL Extension (C++)

The default AOSP VHAL is a simulation. To control real hardware, we extend it:

\`\`\`cpp
// device/brcm/rpi5/vhal/Rpi5VehicleHardware.h
#pragma once

#include <FakeVehicleHardware.h>
#include <atomic>
#include <thread>

namespace android::hardware::automotive::vehicle::V2_0::impl {

/**
 * Custom VHAL implementation for Raspberry Pi 5.
 * 
 * Extends FakeVehicleHardware to add real GPIO control
 * for the HAZARD_LIGHTS_SWITCH property.
 */
class Rpi5VehicleHardware : public FakeVehicleHardware {
public:
    Rpi5VehicleHardware();
    ~Rpi5VehicleHardware() override;
    
    // Override setValues to intercept property changes
    StatusCode setValues(
        std::shared_ptr<const SetValuesCallback> callback,
        const std::vector<SetValueRequest>& requests) override;

private:
    void initGpio();
    bool writeGpio(const char* path, const char* value);
    
    void startHazardBlink();
    void stopHazardBlink();
    
    std::atomic<bool> mHazardBlinkActive{false};
    std::thread mBlinkThread;
    
    // GPIO path for hazard LED (BCM GPIO24 = gpio593 on RPi5)
    static constexpr const char* GPIO_VALUE_PATH = "/sys/class/gpio/gpio593/value";
    static constexpr int32_t HAZARD_LIGHTS_SWITCH = 0x12400E14;  // VehicleProperty ID
};

}  // namespace
\`\`\`

\`\`\`cpp
// device/brcm/rpi5/vhal/Rpi5VehicleHardware.cpp
#include "Rpi5VehicleHardware.h"

#include <android-base/logging.h>
#include <chrono>
#include <fstream>
#include <thread>

namespace android::hardware::automotive::vehicle::V2_0::impl {

Rpi5VehicleHardware::Rpi5VehicleHardware() {
    LOG(INFO) << "Rpi5VehicleHardware initializing";
    initGpio();
}

Rpi5VehicleHardware::~Rpi5VehicleHardware() {
    stopHazardBlink();
}

void Rpi5VehicleHardware::initGpio() {
    // Export GPIO593 (BCM GPIO24)
    std::ofstream exportFile("/sys/class/gpio/export");
    if (exportFile.is_open()) {
        exportFile << "593";
        exportFile.close();
    }
    
    // Set direction
    usleep(100000);
    writeGpio("/sys/class/gpio/gpio593/direction", "out");
    writeGpio(GPIO_VALUE_PATH, "0");
}

bool Rpi5VehicleHardware::writeGpio(const char* path, const char* value) {
    std::ofstream file(path);
    if (!file.is_open()) return false;
    file << value;
    return !file.fail();
}

StatusCode Rpi5VehicleHardware::setValues(
        std::shared_ptr<const SetValuesCallback> callback,
        const std::vector<SetValueRequest>& requests) {
    
    // Intercept HAZARD_LIGHTS_SWITCH property
    for (const auto& request : requests) {
        if (request.value.prop == HAZARD_LIGHTS_SWITCH) {
            int32_t state = request.value.value.int32Values[0];
            LOG(INFO) << "HAZARD_LIGHTS_SWITCH: " << state;
            
            if (state == 1) {
                startHazardBlink();  // Start blinking
            } else {
                stopHazardBlink();   // Stop and turn off
            }
        }
    }
    
    // IMPORTANT: Always delegate to base class
    // This stores the property value and fires callbacks to apps
    return FakeVehicleHardware::setValues(callback, requests);
}

void Rpi5VehicleHardware::startHazardBlink() {
    // Don't start if already running
    if (mHazardBlinkActive.load()) return;
    
    mHazardBlinkActive.store(true);
    
    // Start blink thread
    mBlinkThread = std::thread([this]() {
        LOG(INFO) << "Hazard blink thread started";
        
        while (mHazardBlinkActive.load()) {
            writeGpio(GPIO_VALUE_PATH, "1");  // ON
            std::this_thread::sleep_for(std::chrono::milliseconds(500));
            
            if (!mHazardBlinkActive.load()) break;
            
            writeGpio(GPIO_VALUE_PATH, "0");  // OFF
            std::this_thread::sleep_for(std::chrono::milliseconds(500));
        }
        
        // Ensure LED is off when stopping
        writeGpio(GPIO_VALUE_PATH, "0");
        LOG(INFO) << "Hazard blink thread stopped";
    });
}

void Rpi5VehicleHardware::stopHazardBlink() {
    mHazardBlinkActive.store(false);
    
    if (mBlinkThread.joinable()) {
        mBlinkThread.join();
    }
}

}  // namespace
\`\`\`

---

## Key Differences from Standard HAL

| Aspect | Standard HAL | Vehicle HAL |
|--------|--------------|-------------|
| API Style | Direct methods | Property get/set |
| Data Model | Custom per HAL | Standardized VehicleProperty |
| Subscription | Custom callbacks | Built-in onChange |
| Framework | Custom Manager | CarPropertyManager |
| Target | General Android | Android Automotive only |

---

## VHAL Request/Response Flow

\`\`\`
┌─────────────────────────────────────────────────────────────────────┐
│                    AUTOMOTIVE VHAL FLOW                              │
├─────────────────────────────────────────────────────────────────────┤
│                                                                      │
│  App: setIntProperty(HAZARD_LIGHTS_SWITCH, 0, 1)                     │
│       │                                                              │
│       ▼                                                              │
│  CarPropertyManager (android.car.hardware.property)                  │
│       │  [Binder IPC]                                                │
│       ▼                                                              │
│  CarService / CarPropertyService (com.android.car)                   │
│       │  [HIDL/AIDL IPC]                                             │
│       ▼                                                              │
│  VehicleHAL (Rpi5VehicleHardware.cpp)                                │
│       │                                                              │
│       ├──> setValues() intercepts HAZARD_LIGHTS_SWITCH               │
│       │    └──> startHazardBlink() → GPIO → LED blinks               │
│       │                                                              │
│       └──> FakeVehicleHardware::setValues()                          │
│            └──> Stores value, fires callback to app                  │
│                                                                      │
└─────────────────────────────────────────────────────────────────────┘
\`\`\`

---

## Summary: The Complete Picture

Across this 6-part series, you've learned:

1. **Architecture** - Why Android uses layered security
2. **App & Framework** - Kotlin apps and Java APIs
3. **System Service** - The bridge in system_server
4. **HAL Implementation** - C++ code that touches hardware
5. **SELinux & Init** - Security policies and startup
6. **Automotive VHAL** - Property-based vehicle control

The same patterns apply whether you're building:
- Custom sensors for IoT devices
- Motor controllers for robotics
- Vehicle integrations for automotive
- Any hardware interface on AOSP

---

*Platform: AOSP 15 + Raspberry Pi 5 (BCM2712 + RP1)*  
*All code examples tested on real hardware*
`
};

const BlogPost = () => {
  const { blogId } = useParams();
  const blog = getBlogById(blogId);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [blogId]);

  if (!blog) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Blog post not found</h1>
          <Link to="/blogs" className="text-[var(--accent-primary)] hover:underline">
            ← Back to Blogs
          </Link>
        </div>
      </div>
    );
  }

  const content = blogContent[blogId] || '';
  
  // Get series navigation
  const seriesBlogs = blog.series ? getBlogsBySeries(blog.series) : [];
  const currentIndex = seriesBlogs.findIndex(b => b.id === blogId);
  const prevBlog = currentIndex > 0 ? seriesBlogs[currentIndex - 1] : null;
  const nextBlog = currentIndex < seriesBlogs.length - 1 ? seriesBlogs[currentIndex + 1] : null;

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] pt-20 pb-20">
      {/* Header */}
      <div className="border-b border-[var(--border-subtle)] bg-gradient-to-b from-[var(--accent-subtle)] to-transparent">
        <div className="container py-8 max-w-4xl mx-auto">
          <Link 
            to="/blogs" 
            className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--accent-primary)] transition-colors mb-4"
          >
            <ArrowLeft size={16} />
            Back to Blogs
          </Link>

          {/* Series indicator */}
          {blog.series && (
            <div className="mb-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-[var(--accent-primary)]">
                {blog.series} — Part {blog.partNumber} of {seriesBlogs.length}
              </span>
            </div>
          )}

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {blog.tags.map((tag) => (
              <span 
                key={tag}
                className="text-xs font-medium px-3 py-1 rounded-full bg-[var(--accent-primary)] text-white"
              >
                {tag}
              </span>
            ))}
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-[var(--text-primary)] mb-3">
            {blog.title}
          </h1>
          <p className="text-lg text-[var(--text-secondary)] mb-6">
            {blog.subtitle}
          </p>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-[var(--text-muted)]">
            <span className="flex items-center gap-2">
              <Calendar size={14} />
              {blog.date}
            </span>
            <span className="flex items-center gap-2">
              <Clock size={14} />
              {blog.readTime}
            </span>
            <span>{blog.author}</span>
            <button 
              onClick={handleShare}
              className="flex items-center gap-2 hover:text-[var(--accent-primary)] transition-colors"
            >
              <Share2 size={14} />
              {copied ? 'Copied!' : 'Share'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container py-12 max-w-4xl mx-auto">
        <article className="prose prose-lg max-w-none blog-content">
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({node, ...props}) => <h1 className="text-3xl font-bold text-[var(--text-primary)] mt-10 mb-4" {...props} />,
              h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-[var(--text-primary)] mt-8 mb-4 pb-2 border-b border-[var(--border-subtle)]" {...props} />,
              h3: ({node, ...props}) => <h3 className="text-xl font-semibold text-[var(--text-primary)] mt-6 mb-3" {...props} />,
              p: ({node, ...props}) => <p className="text-[var(--text-secondary)] leading-relaxed mb-4" {...props} />,
              ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-4 text-[var(--text-secondary)]" {...props} />,
              ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-4 text-[var(--text-secondary)]" {...props} />,
              li: ({node, ...props}) => <li className="mb-2" {...props} />,
              code: ({node, inline, ...props}) => 
                inline ? (
                  <code className="bg-[var(--bg-secondary)] px-2 py-1 rounded text-sm font-mono text-[var(--accent-primary)]" {...props} />
                ) : (
                  <code className="block bg-[#1e293b] text-[#e2e8f0] p-4 rounded-lg overflow-x-auto text-sm font-mono" {...props} />
                ),
              pre: ({node, ...props}) => <pre className="bg-[#1e293b] text-[#e2e8f0] p-4 rounded-lg overflow-x-auto mb-6" {...props} />,
              blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-[var(--accent-primary)] pl-4 italic text-[var(--text-muted)] my-6" {...props} />,
              hr: ({node, ...props}) => <hr className="border-[var(--border-subtle)] my-8" {...props} />,
              table: ({node, ...props}) => <div className="overflow-x-auto mb-6"><table className="min-w-full border border-[var(--border-subtle)]" {...props} /></div>,
              th: ({node, ...props}) => <th className="border border-[var(--border-subtle)] px-4 py-2 bg-[var(--bg-secondary)] font-semibold text-left" {...props} />,
              td: ({node, ...props}) => <td className="border border-[var(--border-subtle)] px-4 py-2" {...props} />,
              strong: ({node, ...props}) => <strong className="font-semibold text-[var(--text-primary)]" {...props} />,
              a: ({node, ...props}) => <a className="text-[var(--accent-primary)] hover:underline" {...props} />,
              img: ({node, ...props}) => <img className="w-full rounded-lg shadow-lg my-6" {...props} />,
            }}
          >
            {content}
          </ReactMarkdown>
        </article>

        {/* Series Navigation */}
        {blog.series && (prevBlog || nextBlog) && (
          <div className="mt-12 pt-8 border-t border-[var(--border-subtle)]">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-4">
              Continue Reading
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {prevBlog && (
                <Link 
                  to={`/blog/${prevBlog.id}`}
                  className="group p-4 border border-[var(--border-subtle)] rounded-lg hover:border-[var(--accent-primary)] transition-colors"
                >
                  <div className="flex items-center gap-2 text-sm text-[var(--text-muted)] mb-2">
                    <ArrowLeft size={14} />
                    Previous
                  </div>
                  <div className="font-medium text-[var(--text-primary)] group-hover:text-[var(--accent-primary)] transition-colors">
                    Part {prevBlog.partNumber}: {prevBlog.title.split(' - ')[1] || prevBlog.title}
                  </div>
                </Link>
              )}
              {nextBlog && (
                <Link 
                  to={`/blog/${nextBlog.id}`}
                  className="group p-4 border border-[var(--border-subtle)] rounded-lg hover:border-[var(--accent-primary)] transition-colors md:text-right"
                >
                  <div className="flex items-center justify-end gap-2 text-sm text-[var(--text-muted)] mb-2">
                    Next
                    <ArrowRight size={14} />
                  </div>
                  <div className="font-medium text-[var(--text-primary)] group-hover:text-[var(--accent-primary)] transition-colors">
                    Part {nextBlog.partNumber}: {nextBlog.title.split(' - ')[1] || nextBlog.title}
                  </div>
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Footer CTA */}
        <div className="mt-12 pt-8 border-t border-[var(--border-subtle)]">
          <div className="bg-gradient-to-r from-[var(--accent-subtle)] to-transparent rounded-xl p-8">
            <h3 className="text-xl font-bold text-[var(--text-primary)] mb-3">
              Need help with Android Automotive or embedded systems?
            </h3>
            <p className="text-[var(--text-secondary)] mb-6">
              Our team has deep expertise in AOSP, VHAL, custom HALs, and automotive software engineering.
            </p>
            <Link 
              to="/#contact" 
              className="btn-primary inline-flex items-center gap-2"
            >
              Let's Talk
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
